import {
  ArrowRightLeft,
  FileText,
  FolderTree,
  LayoutTemplate,
  Megaphone,
  Menu as MenuIcon,
  Newspaper,
  SearchCheck,
  SquareStack,
} from 'lucide-react';

import { defineTour, type TourResolver } from '@nucleus-commerce/admin-kit/tour';
import {
  getAdminV1OrgBlocks,
  getAdminV1StoresStoreIdBlocks,
  getAdminV1StoresStoreIdBlogPosts,
  getAdminV1StoresStoreIdMenus,
  getAdminV1StoresStoreIdPages,
  getAdminV1StoresStoreIdSections,
} from '@nucleus-commerce/api-client/admin';

const newestPage: TourResolver = {
  key: 'newest-page',
  thing: 'things.page',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdPages({ storeId }, { limit: 1, sort: '-updatedAt' });
    const row = page.items[0];
    return row ? { storeId, pageId: row.id } : null;
  },
};

const newestPost: TourResolver = {
  key: 'newest-post',
  thing: 'things.blogPost',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdBlogPosts(
      { storeId },
      { limit: 1, sort: '-updatedAt' },
    );
    const row = page.items[0];
    return row ? { storeId, postId: row.id } : null;
  },
};

/** Menus are a short unpaged list; the one edited last is the likeliest to have links. */
const newestMenu: TourResolver = {
  key: 'newest-menu',
  thing: 'things.menu',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const { items } = await getAdminV1StoresStoreIdMenus({ storeId });
    const row = items.reduce<(typeof items)[number] | undefined>(
      (latest, menu) => (!latest || menu.updatedAt > latest.updatedAt ? menu : latest),
      undefined,
    );
    return row ? { storeId, menuId: row.id } : null;
  },
};

/** Only this store's own blocks: a shared one opens read-only inside a store. */
const newestStoreBlock: TourResolver = {
  key: 'newest-store-block',
  thing: 'things.block',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdBlocks(
      { storeId },
      { limit: 1, sort: '-updatedAt', scope: 'store' },
    );
    const row = page.items[0];
    return row ? { storeId, blockId: row.id } : null;
  },
};

const newestSection: TourResolver = {
  key: 'newest-section',
  thing: 'things.section',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdSections(
      { storeId },
      { limit: 1, sort: '-updatedAt' },
    );
    const row = page.items[0];
    return row ? { storeId, sectionId: row.id } : null;
  },
};

const newestSharedBlock: TourResolver = {
  key: 'newest-shared-block',
  thing: 'things.sharedBlock',
  load: async () => {
    const page = await getAdminV1OrgBlocks({ limit: 1, sort: '-updatedAt' });
    const row = page.items[0];
    return row ? { blockId: row.id } : null;
  },
};

export const pagesTour = defineTour({
  id: 'content.pages',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/pages/',
  scope: 'store',
  requires: 'content.read',
  icon: FileText,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'content.pages.table' },
        { target: 'content.pages.empty', copy: 'empty' },
      ],
    },
    {
      id: 'row-actions',
      kind: 'explain',
      target: 'content.pages.table',
      requires: 'content.write',
      optional: true,
    },
    { id: 'new', kind: 'action', target: 'content.pages.new', requires: 'content.write' },
  ],
  related: ['content.page-editor', 'content.menus'],
});

export const pageEditorTour = defineTour({
  id: 'content.page-editor',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/pages/$pageId',
  scope: 'record',
  resolve: newestPage,
  requires: 'content.read',
  icon: FileText,
  steps: [
    { id: 'details', kind: 'explain', target: 'content.page-editor.details', placement: 'right' },
    {
      id: 'template',
      kind: 'explain',
      target: 'content.page-editor.template',
      placement: 'right',
    },
    { id: 'body', kind: 'explain', target: 'content.page-editor.body', placement: 'right' },
    {
      id: 'blocks',
      kind: 'explain',
      target: 'content.page-editor.blocks',
      placement: 'right',
      optional: true,
    },
    {
      id: 'seo-preview',
      kind: 'explain',
      target: 'content.page-editor.seo-preview',
      placement: 'left',
    },
    {
      id: 'save',
      kind: 'explain',
      target: 'content.page-editor.save',
      placement: 'left',
      requires: 'content.write',
    },
  ],
  related: ['content.menus'],
});

export const blogTour = defineTour({
  id: 'content.blog',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/blog/',
  scope: 'store',
  requires: 'content.read',
  icon: Newspaper,
  steps: [
    {
      id: 'list',
      kind: 'explain',
      variants: [{ target: 'content.blog.table' }, { target: 'content.blog.empty', copy: 'empty' }],
    },
    { id: 'categories', kind: 'explain', target: 'content.blog.categories-link' },
    { id: 'new', kind: 'action', target: 'content.blog.new', requires: 'content.write' },
  ],
  related: ['content.blog-post', 'content.blog-categories'],
});

export const blogPostTour = defineTour({
  id: 'content.blog-post',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/blog/$postId',
  scope: 'record',
  resolve: newestPost,
  requires: 'content.read',
  icon: Newspaper,
  steps: [
    { id: 'content', kind: 'explain', target: 'content.blog-post.content', placement: 'right' },
    {
      id: 'featured-image',
      kind: 'explain',
      target: 'content.blog-post.featured-image',
      placement: 'right',
    },
    {
      id: 'organisation',
      kind: 'explain',
      target: 'content.blog-post.organisation',
      placement: 'right',
    },
    {
      id: 'publishing',
      kind: 'explain',
      target: 'content.blog-post.publishing',
      placement: 'left',
    },
    {
      id: 'save',
      kind: 'explain',
      target: 'content.blog-post.save',
      placement: 'left',
      requires: 'content.write',
    },
  ],
  related: ['content.blog'],
});

export const blogCategoriesTour = defineTour({
  id: 'content.blog-categories',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/blog/categories',
  scope: 'store',
  requires: 'content.read',
  icon: FolderTree,
  steps: [
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'content.blog-categories.table' },
        { target: 'content.blog-categories.empty', copy: 'empty' },
      ],
    },
    { id: 'header', kind: 'explain', target: 'content.blog-categories.header' },
    {
      id: 'new',
      kind: 'action',
      target: 'content.blog-categories.new',
      requires: 'content.write',
    },
  ],
  related: ['content.blog'],
});

export const menusTour = defineTour({
  id: 'content.menus',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/menus/',
  scope: 'store',
  requires: 'content.read',
  icon: MenuIcon,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'content.menus.table' },
        { target: 'content.menus.empty', copy: 'empty' },
      ],
    },
    { id: 'new', kind: 'action', target: 'content.menus.new', requires: 'content.write' },
  ],
  related: ['content.menu-editor'],
});

export const menuEditorTour = defineTour({
  id: 'content.menu-editor',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/menus/$menuId',
  scope: 'record',
  resolve: newestMenu,
  requires: 'content.read',
  icon: MenuIcon,
  steps: [
    { id: 'tree', kind: 'explain', target: 'content.menu-editor.tree', placement: 'top' },
    {
      id: 'node-actions',
      kind: 'explain',
      target: 'content.menu-editor.node-actions',
      requires: 'content.write',
      optional: true,
    },
    {
      id: 'add-item',
      kind: 'explain',
      target: 'content.menu-editor.add-item',
      requires: 'content.write',
    },
    {
      id: 'save',
      kind: 'explain',
      target: 'content.menu-editor.save',
      requires: 'content.write',
    },
  ],
  related: ['content.menus'],
});

export const blocksTour = defineTour({
  id: 'content.blocks',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/blocks/',
  scope: 'store',
  requires: 'content.read',
  icon: SquareStack,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'content.blocks.table' },
        { target: 'content.blocks.empty', copy: 'empty' },
      ],
    },
    { id: 'new', kind: 'action', target: 'content.blocks.new', requires: 'content.write' },
  ],
  related: ['content.block-new', 'content.block-editor'],
});

export const blockNewTour = defineTour({
  id: 'content.block-new',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/blocks/new/',
  scope: 'store',
  requires: 'content.write',
  icon: SquareStack,
  steps: [
    { id: 'banner', kind: 'explain', target: 'content.block-new.kind-banner' },
    { id: 'rail', kind: 'explain', target: 'content.block-new.kind-rail' },
    { id: 'kinds', kind: 'explain', target: 'content.block-new.kinds' },
  ],
  related: ['content.block-editor'],
});

export const blockEditorTour = defineTour({
  id: 'content.block-editor',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/blocks/$blockId',
  scope: 'record',
  resolve: newestStoreBlock,
  requires: 'content.read',
  icon: SquareStack,
  steps: [
    {
      id: 'details',
      kind: 'explain',
      target: 'content.block-editor.details',
      placement: 'right',
    },
    {
      id: 'content',
      kind: 'explain',
      placement: 'right',
      variants: [
        { target: 'content.block-editor.slides' },
        { target: 'content.block-editor.rail-source', copy: 'rail' },
      ],
    },
    {
      id: 'preview',
      kind: 'explain',
      target: 'content.block-editor.preview',
      placement: 'left',
    },
    {
      id: 'schedule',
      kind: 'explain',
      target: 'content.block-editor.schedule',
      placement: 'left',
    },
    {
      id: 'warnings',
      kind: 'explain',
      target: 'content.block-editor.warnings',
      optional: true,
      waitMs: 1000,
    },
    {
      id: 'save',
      kind: 'explain',
      target: 'content.block-editor.save',
      placement: 'left',
      requires: 'content.write',
    },
  ],
  related: ['content.sections'],
});

export const sectionsTour = defineTour({
  id: 'content.sections',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/sections/',
  scope: 'store',
  requires: 'content.read',
  icon: LayoutTemplate,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'content.sections.table' },
        { target: 'content.sections.empty', copy: 'empty' },
      ],
    },
    { id: 'new', kind: 'action', target: 'content.sections.new', requires: 'content.write' },
  ],
  related: ['content.section-editor'],
});

export const sectionEditorTour = defineTour({
  id: 'content.section-editor',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/sections/$sectionId',
  scope: 'record',
  resolve: newestSection,
  requires: 'content.read',
  icon: LayoutTemplate,
  steps: [
    {
      id: 'details',
      kind: 'explain',
      target: 'content.section-editor.details',
      placement: 'right',
    },
    { id: 'blocks', kind: 'explain', target: 'content.section-editor.blocks', placement: 'top' },
    {
      id: 'add-blocks',
      kind: 'explain',
      target: 'content.section-editor.add-blocks',
      requires: 'content.write',
    },
    {
      id: 'save-blocks',
      kind: 'explain',
      target: 'content.section-editor.save-blocks',
      requires: 'content.write',
    },
    { id: 'preview', kind: 'explain', target: 'content.section-editor.preview' },
  ],
  related: ['content.blocks', 'shared-blocks.list'],
});

export const redirectsTour = defineTour({
  id: 'content.redirects',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/redirects',
  scope: 'store',
  requires: 'content.read',
  icon: ArrowRightLeft,
  steps: [
    { id: 'intro', kind: 'explain', target: 'content.redirects.intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'content.redirects.table' },
        { target: 'content.redirects.empty', copy: 'empty' },
      ],
    },
    { id: 'add', kind: 'explain', target: 'content.redirects.add', requires: 'content.write' },
  ],
  related: ['content.seo'],
});

export const seoTour = defineTour({
  id: 'content.seo',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/s/$storeId/content/seo',
  scope: 'store',
  requires: 'content.read',
  icon: SearchCheck,
  steps: [
    {
      id: 'issues',
      kind: 'explain',
      variants: [
        { target: 'content.seo.issues-table' },
        { target: 'content.seo.empty', copy: 'empty' },
      ],
    },
    { id: 'audits', kind: 'explain', target: 'content.seo.audits', placement: 'left' },
    {
      id: 'indexnow',
      kind: 'explain',
      target: 'content.seo.indexnow',
      placement: 'left',
      optional: true,
    },
    { id: 'feeds', kind: 'explain', target: 'content.seo.feeds-link', placement: 'left' },
  ],
  related: ['settings.feeds'],
});

export const sharedBlocksTour = defineTour({
  id: 'shared-blocks.list',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/org/shared-blocks/',
  scope: 'org',
  requires: 'content.read',
  icon: Megaphone,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'shared-blocks.list.table' },
        { target: 'shared-blocks.list.empty', copy: 'empty' },
      ],
    },
    { id: 'new', kind: 'action', target: 'shared-blocks.list.new', requires: 'content.write' },
  ],
  related: ['shared-blocks.editor', 'content.section-editor'],
});

export const sharedBlockEditorTour = defineTour({
  id: 'shared-blocks.editor',
  version: 1,
  group: 'marketing',
  route: '/_authed/_active/org/shared-blocks/$blockId',
  scope: 'record',
  resolve: newestSharedBlock,
  requires: 'content.read',
  icon: Megaphone,
  steps: [
    {
      id: 'details',
      kind: 'explain',
      target: 'shared-blocks.editor.details',
      placement: 'right',
    },
    {
      id: 'preview',
      kind: 'explain',
      target: 'shared-blocks.editor.preview',
      placement: 'left',
    },
    {
      id: 'schedule',
      kind: 'explain',
      target: 'shared-blocks.editor.schedule',
      placement: 'left',
    },
    {
      id: 'save',
      kind: 'explain',
      target: 'shared-blocks.editor.save',
      placement: 'left',
      requires: 'content.write',
    },
  ],
  related: ['shared-blocks.list'],
});
